import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import DossierPret from "./DossierPret";

const GAINS = [
  {
    t: "Plus rien sur une boîte Gmail",
    d: "Ordonnance, carte de mutuelle, attestation : le patient les dépose depuis la page de votre établissement. Elles arrivent dans le rendez-vous, pas dans une boîte grand public partagée par toute l'équipe.",
  },
  {
    t: "Un seul canal, dans les deux sens",
    d: "Il manque une pièce ? Vous écrivez depuis le rendez-vous. Le patient répond au même endroit. Ni votre adresse personnelle, ni un numéro de portable, ni un SMS perdu.",
  },
  {
    t: "Vous vérifiez avant qu'il pousse la porte",
    d: "Les documents sont là la veille. Vous les ouvrez, vous repérez l'oubli, vous lui demandez la pièce manquante. Le jour du rendez-vous, plus de surprise au comptoir.",
  },
];

const BAND = [
  ["Chiffré", "intercepté, un document reste illisible."],
  ["Au bon moment", "la fiche est remplie avant la visite, pas au comptoir."],
  ["Au bon endroit", "le document et l'échange vivent dans le rendez-vous."],
];

const PATIENT = [
  ["Il retrouve l'historique de ses rendez-vous et son parcours de soins."],
  ["Il dépose ses documents quand il veut, depuis son téléphone, avant de venir."],
  ["Il reçoit vos messages au même endroit, sans donner son adresse à qui que ce soit."],
  [
    "Il s'identifiera avec FranceConnect+ : c'est bien lui, et aucun mot de passe à gérer.",
    "Bientôt disponible",
  ],
];

export default function DossierPage() {
  return (
    <div className="dsp">
      <SEO
        title="Le patient arrive avec son dossier déjà prêt · MetaConnect"
        description="La prise de rendez-vous crée la fiche du patient : il la remplit, dépose son ordonnance, vous consultez avant son passage. Un seul canal sécurisé."
        canonical="/dossier-patient"
      />
      <style>{CSS}</style>

      <section className="dsp-hero">
        <div className="dsp-wrap">
          <span className="dsp-pill">Espace patient</span>
          <h1>Le patient arrive avec son dossier déjà prêt</h1>
          <p className="dsp-lede">
            La prise de rendez-vous ne réserve pas qu'un créneau : elle crée la fiche du patient.
            Il la remplit, dépose son ordonnance et sa mutuelle, et vous consultez tout avant son
            passage — dans un canal sécurisé, à un seul endroit.
          </p>
          <div className="dsp-flag">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            Plus aucun document sur une boîte Gmail
          </div>
        </div>
      </section>

      <section className="dsp-sec">
        <div className="dsp-wrap">
          <div className="dsp-head">
            <h2>De sa demande à votre comptoir, sans un seul mail perdu</h2>
            <p>Quatre gestes, du côté du patient comme du vôtre.</p>
          </div>
          <DossierPret />
        </div>
      </section>

      <section className="dsp-sec dsp-alt">
        <div className="dsp-wrap">
          <div className="dsp-head">
            <h2>Ce que ça change dans votre magasin</h2>
          </div>
          <div className="dsp-grid">
            {GAINS.map((g) => (
              <div className="dsp-card" key={g.t}>
                <h3>{g.t}</h3>
                <p>{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dsp-sec dsp-bandsec">
        <div className="dsp-wrap">
          <div className="dsp-band">
            <h2>Le chiffrement est la première clé contre les cyberattaques</h2>
            <p>
              Une donnée de santé chiffrée reste illisible pour qui l'intercepte : c'est la seule
              parade qui tienne. Et ce même canal fait plus que protéger — il réunit les bonnes
              informations, au bon moment, au bon endroit.
            </p>
            <ul className="dsp-bl">
              {BAND.map(([k, v]) => (
                <li key={k}>
                  <b>{k}</b>
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="dsp-sec">
        <div className="dsp-wrap">
          <div className="dsp-pat">
            <div className="dsp-patl">
              <h2>Ce que le patient voit de son côté</h2>
              <p>
                Un espace à lui, pas une boîte mail de plus. C'est là qu'il prend rendez-vous, qu'il
                dépose et qu'il vous répond.
              </p>
            </div>
            <ul className="dsp-list">
              {PATIENT.map(([x, soon]) => (
                <li key={x} className={soon ? "dsp-li-soon" : ""}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>
                    {x}
                    {soon ? <em className="dsp-soon">{soon}</em> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="dsp-more">
            <Link to="/dmp">
              Le dossier médical partagé, en consultation et en alimentation
              <em>&rsaquo;</em>
            </Link>
            <Link to="/message-type">
              Le message envoyé à l'ophtalmologiste ou à l'orthoptiste
              <em>&rsaquo;</em>
            </Link>
          </div>
        </div>
      </section>

      <section className="dsp-cta">
        <div className="dsp-wrap">
          <h2>Vous voulez le voir tourner sur vos propres rendez-vous ?</h2>
          <p>Quinze minutes suffisent pour comprendre le parcours complet.</p>
          <Link className="dsp-btn" to="/demo">
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </div>
  );
}

const CSS = `
.dsp{
  --ground:#F5F5F5;--surface:#fff;--line:#E6E8EC;--text:#0F172A;--muted:#5A6478;
  --blue:#0080FF;--bsoft:#EAF4FF;--bline:#CFE4FF;
  background:var(--ground);color:var(--text);
  font-family:"Instrument Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.dsp *{box-sizing:border-box}
.dsp h1,.dsp h2,.dsp h3{margin:0;color:var(--text);font-weight:600;text-wrap:balance}
.dsp h1{font-size:clamp(29px,3.6vw,40px);line-height:1.14;letter-spacing:-.02em}
.dsp p{margin:0;color:var(--muted);line-height:1.6}
.dsp ul{list-style:none;margin:0;padding:0}
.dsp-wrap{max-width:1000px;margin:0 auto;padding:0 28px}

.dsp-pill{display:inline-flex;border-radius:999px;background:#E7ECF3;color:#45526B;
  font-size:11.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.dsp-hero{padding:82px 0 34px;text-align:center}
.dsp-hero h1{margin:16px auto 0;max-width:20ch}
.dsp .dsp-lede{max-width:66ch;margin:16px auto 0;font-size:16.5px}
.dsp-flag{display:inline-flex;align-items:center;gap:9px;background:#FEF2F2;border:1px solid #F5C2C2;
  color:#9B2C2C;border-radius:999px;font-size:12.8px;font-weight:600;padding:8px 16px;margin-top:22px}
.dsp-flag svg{width:14px;height:14px;stroke:currentColor;fill:none;stroke-width:2.6;
  stroke-linecap:round;flex:0 0 14px}

.dsp-sec{padding:52px 0}
.dsp-alt{background:#fff;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.dsp-head{text-align:center;max-width:64ch;margin:0 auto}
.dsp-head h2{font-size:clamp(22px,2.6vw,30px);line-height:1.2;letter-spacing:-.018em}
.dsp-head p{margin-top:12px;font-size:15.5px}

.dsp-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:30px}
.dsp-card{background:var(--ground);border:1px solid var(--line);border-radius:14px;padding:22px 22px 24px}
.dsp-card h3{font-size:16px;line-height:1.3;margin-bottom:9px}
.dsp-card p{font-size:13.8px;line-height:1.62}

.dsp-bandsec{padding-top:0}
.dsp-band{background:#0F1B33;border-radius:18px;padding:34px 36px;text-align:center}
.dsp .dsp-band h2{color:#fff;font-size:clamp(21px,2.4vw,27px);line-height:1.22;letter-spacing:-.015em}
.dsp .dsp-band p{color:rgba(255,255,255,.78);font-size:15.5px;max-width:62ch;margin:12px auto 0}
.dsp-bl{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:24px;text-align:left}
.dsp-bl li{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.14);border-radius:12px;
  padding:14px 16px;font-size:13.4px;line-height:1.5;color:#C8D4E6}
.dsp-bl b{display:block;color:#fff;font-size:13.8px;margin-bottom:3px}

.dsp-pat{display:grid;grid-template-columns:300px 1fr;gap:28px;align-items:center;background:#fff;
  border:1px solid var(--line);border-radius:18px;padding:30px 32px}
.dsp-patl h2{font-size:22px;line-height:1.22;letter-spacing:-.015em}
.dsp-patl p{margin-top:10px;font-size:14px}
.dsp-list{display:grid;gap:12px}
.dsp-list li{display:flex;gap:12px;align-items:flex-start;font-size:14.2px;line-height:1.55;
  color:#3D4759}
.dsp-soon{display:inline-block;font-style:normal;font-size:10.4px;font-weight:700;letter-spacing:.04em;
  text-transform:uppercase;background:#FEF6E7;color:#8A6414;border:1px solid #F3DCA8;border-radius:999px;
  padding:2px 8px;margin-left:8px;vertical-align:2px;white-space:nowrap}
.dsp-li-soon{color:#8A93A5}
.dsp-li-soon svg{stroke:#B4BCC9}
.dsp-list svg{width:16px;height:16px;flex:0 0 16px;margin-top:3px;stroke:#1E8A4C;fill:none;
  stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}

.dsp-more{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-top:20px}
.dsp-more a{display:flex;align-items:center;justify-content:space-between;gap:12px;
  background:var(--bsoft);border:1px solid var(--bline);border-radius:13px;padding:16px 20px;
  text-decoration:none;color:#0B62C4;font-size:14.4px;font-weight:600;line-height:1.4;
  transition:background .18s}
.dsp-more a:hover{background:#DBEBFF}
.dsp-more em{font-style:normal;font-size:19px;line-height:1}

.dsp-cta{background:var(--blue);padding:56px 0;text-align:center}
.dsp-cta h2{color:#fff;font-size:clamp(22px,2.6vw,30px);margin-bottom:12px}
.dsp-cta p{color:rgba(255,255,255,.9);font-size:15.5px;max-width:60ch;margin:0 auto 24px}
.dsp-btn{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;
  background:#fff;color:var(--blue);font-weight:600;font-size:15px;text-decoration:none;
  padding:13px 28px}
.dsp-btn:hover{background:#EAF4FF;color:var(--blue)}

@media (max-width:880px){
  .dsp-grid,.dsp-bl{grid-template-columns:1fr}
  .dsp-band{padding:28px 22px}
  .dsp-pat{grid-template-columns:1fr;gap:20px;align-items:start}
  .dsp-more{grid-template-columns:1fr}
  .dsp-hero{padding:56px 0 28px}
}
.dsp a:focus-visible{outline:2px solid var(--blue);outline-offset:3px;border-radius:6px}
`;
